import { useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import ApexCharts from "apexcharts";
import { Download } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TimeWindow,
  buildMeasurementCsv,
  downloadFile,
  exportFileName,
  exportWindow,
  pointsInWindow,
} from "./measurementExport";
import { ChartSeries } from "./measurementSeries";

interface MeasurementExportMenuProperties {
  chartId: string;
  assetId: string;
  metric: string;
  /** Localised name of the metric (second CSV column header). */
  label: string;
  unit?: string;
  series: ChartSeries;
  /** The selected period. */
  period: TimeWindow;
  /** The zoomed window, if the chart is zoomed. */
  zoom?: TimeWindow | null;
}

const csvBlob = (csv: string): Blob => new Blob([csv], { type: "text/csv;charset=utf-8" });

/**
 * The Export button of a chart (tri-server #1590). It replaces the small download icon of the
 * ApexCharts toolbar: a real button with a touch-sized target, and a CSV Excel opens correctly.
 */
export default function MeasurementExportMenu(properties: MeasurementExportMenuProperties) {
  const { chartId, assetId, metric, label, unit, series, period, zoom } = properties;
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  const visible: TimeWindow = zoom ?? period;

  const exportCsv = (wholePeriod: boolean) => {
    try {
      const points = pointsInWindow(series, wholePeriod ? null : visible);
      const csv = buildMeasurementCsv({
        points,
        language: i18next.language,
        labels: {
          time: t("assetPropertiesPanel.export.columnTime"),
          metric: label,
          unit: t("assetPropertiesPanel.export.columnUnit"),
        },
        unit,
      });
      const named = exportWindow(wholePeriod ? period : visible, points);
      downloadFile(exportFileName(assetId, metric, named, "csv"), csvBlob(csv));
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  };

  const named = exportWindow(visible, pointsInWindow(series, visible));

  const exportImage = async (kind: "png" | "svg") => {
    try {
      if (kind === "png") {
        const result: any = await ApexCharts.exec(chartId, "dataURI", { scale: 2 });
        if (!result?.imgURI && !result?.blob) {
          throw new Error("no image");
        }
        downloadFile(exportFileName(assetId, metric, named, "png"), result.imgURI ?? result.blob);
      } else {
        // getSvgString is not on ApexCharts' exec whitelist; the instance has it.
        const chart: any = ApexCharts.getChartByID(chartId);
        if (!chart || typeof chart.getSvgString !== "function") {
          throw new Error("no chart");
        }
        const svg: string = await chart.getSvgString();
        downloadFile(
          exportFileName(assetId, metric, named, "svg"),
          new Blob([svg], { type: "image/svg+xml;charset=utf-8" }),
        );
      }
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`export-${metric}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm">
            <Download aria-hidden="true" className="mr-2 h-4 w-4" />
            {t("assetPropertiesPanel.export.button")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem className="min-h-11" onSelect={() => exportCsv(false)}>
            {t("assetPropertiesPanel.export.csvWindow")}
          </DropdownMenuItem>
          <DropdownMenuItem className="min-h-11" onSelect={() => exportCsv(true)}>
            {t("assetPropertiesPanel.export.csvPeriod")}
          </DropdownMenuItem>
          <DropdownMenuItem className="min-h-11" onSelect={() => exportImage("png")}>
            PNG
          </DropdownMenuItem>
          <DropdownMenuItem className="min-h-11" onSelect={() => exportImage("svg")}>
            SVG
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {failed && (
        <span role="alert" className="text-xs text-destructive" data-testid={`export-failed-${metric}`}>
          {t("assetPropertiesPanel.export.failed")}
        </span>
      )}
    </div>
  );
}
